import {
  getGetApiDeviceQueryOptions,
  getGetApiGroupQueryOptions,
  getGetApiInstitutionQueryOptions,
  getGetApiLabQueryOptions,
  type DeviceViewDTO,
} from '@smartsash/api-client';
import type { TableFilterFieldOverrides } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import type { TableMeta } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEVICES_GROUP_FIELD,
  DEVICES_HOOD_TYPE_FIELD,
  DEVICES_INSTITUTION_FIELD,
  DEVICES_LAB_FIELD,
  DEVICES_ONLINE_FIELD,
  deviceHoodTypeOptions,
  devicesColumnText,
  devicesQueryParams,
  type DevicesSearch,
} from './devicesModel';

import { LOOKUP_PAGE_SIZE, useLabNames } from '@/hooks/useLookupNames';
import { DataScreenFilter, filterColumnsFromText } from '@/ui/shared/TableFilter';

/**
 * The filter builder for the Devices list (HS-869 + the filtering-foundation follow-up).
 *
 * Devices is the screen with the most server-side filters: `GetApiDeviceParams` accepts seven, and
 * four of them — institution, group, hood type and online status — have no column of their own, so
 * they are appended as extra fields. The rows come from the screen's own query key —
 * `devicesQueryParams` builds it identically here and in `devicesScreen` — which means this is a
 * cache read rather than a second request.
 */
export function DevicesFilter({ search }: { search: DevicesSearch }) {
  const { t } = useTranslation();
  const { data } = useQuery(getGetApiDeviceQueryOptions(devicesQueryParams(search)));
  const { data: labs } = useQuery(
    getGetApiLabQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const { data: institutions } = useQuery(
    getGetApiInstitutionQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const { data: groups } = useQuery(
    getGetApiGroupQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const labNames = useLabNames();
  const meta = useMemo<TableMeta<DeviceViewDTO>>(() => ({ labNames }), [labNames]);

  const columns = useMemo(
    () =>
      filterColumnsFromText<DeviceViewDTO>(
        {
          // HS-1216: `id` is a column but deliberately not a field here. `GET /api/Device` has no
          // `id` parameter, so `buildFilterParams` would drop an `eq` row silently, and
          // `collectSearchTerms` would push a `contains` row into the cross-field `search`, which
          // the backend matches against model/serial/identifier/MAC and never against the id. The
          // filter would look applied and do nothing — exactly the defect HS-1241 was filed for.
          // Guarded by `DevicesFilter.test.tsx`.
          model: devicesColumnText.model,
          laboratory: (row) => devicesColumnText.laboratory(row, meta),
          serialNumber: devicesColumnText.serialNumber,
          identifier: devicesColumnText.identifier,
          firmwareVersion: devicesColumnText.firmwareVersion,
          osVersion: devicesColumnText.osVersion,
        },
        // `firmwareVersion`/`osVersion` reuse the detail page's `devices.details.*` keys, the same
        // departure from `devices.columns.*` the column headers make — see `devicesScreen.tsx`.
        (id) =>
          id === 'firmwareVersion' || id === 'osVersion'
            ? t(`devices.details.${id}`)
            : t(`devices.columns.${id as 'model'}`),
      ),
    [meta, t],
  );

  const overrides = useMemo<TableFilterFieldOverrides>(
    () => ({
      labels: { [DEVICES_LAB_FIELD]: t('devices.filters.laboratory') },
      options: {
        // The column renders lab names; the query takes lab ids.
        [DEVICES_LAB_FIELD]: (labs?.items ?? [])
          .filter((lab) => lab.id != null && lab.name != null)
          .map((lab) => ({ value: lab.id ?? '', label: lab.name ?? '' })),
      },
      extra: [
        {
          id: DEVICES_INSTITUTION_FIELD,
          label: t('devices.filters.institute'),
          type: 'enum',
          options: (institutions?.items ?? [])
            .filter((institution) => institution.id != null && institution.name != null)
            .map((institution) => ({
              value: institution.id ?? '',
              label: institution.name ?? '',
            })),
        },
        {
          id: DEVICES_GROUP_FIELD,
          label: t('devices.filters.group'),
          type: 'enum',
          options: (groups?.items ?? [])
            .filter((group) => group.id != null && group.name != null)
            .map((group) => ({ value: group.id ?? '', label: group.name ?? '' })),
        },
        {
          id: DEVICES_HOOD_TYPE_FIELD,
          label: t('devices.filters.hoodType'),
          type: 'enum',
          options: deviceHoodTypeOptions(),
        },
        {
          id: DEVICES_ONLINE_FIELD,
          label: t('devices.filters.status'),
          type: 'enum',
          options: [
            { value: 'true', label: t('devices.filters.online') },
            { value: 'false', label: t('devices.filters.offline') },
          ],
        },
      ],
    }),
    [groups, institutions, labs, t],
  );

  return <DataScreenFilter columns={columns} rows={data?.items ?? []} overrides={overrides} />;
}
